// src/components/TemplateEditor.jsx
import React, { useEffect, useMemo, useState } from "react";

/* --------------------------- Utils --------------------------- */
function toEmailHtml(body = "") {
  const isHtml = /<[^>]+>/g.test(body);
  if (isHtml) return body;
  const esc = (s = "") =>
    String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return esc(body).replace(/\n/g, "<br/>");
}

/* =============================================================
   TemplateEditor (v2) — Previews refinadas (WA + Email)
   ============================================================= */
function TemplateEditor({
  template = "",
  setTemplate = () => { }, // opcional, por compatibilidad
  columns = [],
  compact = false,
  showEmail = false,
  hideWhatsApp = false,     // 👈 nuevo parámetro
  emailSubject = "",
  emailBody = "",
}) {
  const [preview, setPreview] = useState("");
  const [emailPreview, setEmailPreview] = useState("");
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    const esc = (s = "") =>
      String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const placeholders = [];
    const hold = (html) => {
      const key = `__CODE_BLOCK_${placeholders.length}__`;
      placeholders.push({ key, html });
      return key;
    };

    let txt = esc(template ?? "");
    txt = txt.replace(/```([\s\S]*?)```/g, (_, c) => hold(`<pre class="wa-mono">${c}</pre>`));
    txt = txt.replace(/\*(.+?)\*/g, "<strong>$1</strong>");
    txt = txt.replace(/_(.+?)_/g, "<em>$1</em>");
    txt = txt.replace(/~(.+?)~/g, "<span class='wa-strike'>$1</span>");
    txt = txt.replace(/\{([a-zA-Z0-9_\-]+)\}/g, "<span class='wa-var'>{$1}</span>");
    txt = txt.replace(/\n/g, "<br/>");
    for (const ph of placeholders) txt = txt.replace(ph.key, ph.html);
    setPreview(txt);
  }, [template]);

  useEffect(() => {
    setEmailPreview(toEmailHtml(emailBody));
  }, [emailBody]);

  const varChips = useMemo(() => (columns || []).filter(Boolean).slice(0, 24), [columns]);

  return (
    <section className={`card section !p-4 md:!p-6 ${compact ? "wa-compact" : ""}`}>
      <style>{`
        .hint{font-size:11px;color:var(--muted)}
        .wa-strike{text-decoration:line-through;opacity:.9}
        .wa-var{background:#FDE68A;color:#111827;padding:0 4px;border-radius:4px}
        .wa-mono{background:#111827;color:#E5E7EB;border-radius:8px;padding:8px 10px;white-space:pre-wrap;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace}
        .phone{width:360px;max-width:100%;border-radius:28px;padding:12px;background:#0b141a;border:1px solid #111827;box-shadow:0 12px 30px rgba(0,0,0,.35)}
        .phone-screen{border-radius:20px;overflow:hidden;background:#0b141a;border:1px solid #1f2937}
        .phone-header{height:48px;background:#202c33;color:#e5e7eb;display:flex;align-items:center;gap:10px;padding:0 12px;border-bottom:1px solid #1f2937;font-size:14px}
        .phone-avatar{width:28px;height:28px;border-radius:50%;background:#2a3942}
        .phone-chat{position:relative;padding:12px;min-height:220px;background:radial-gradient(circle at 20% 20%, rgba(255,255,255,0.03) 0 10%, transparent 11%) 0 0/24px 24px,#0b141a}
        .bubble-row{display:flex;justify-content:flex-end;margin:6px 0;padding-left:46px}
        .bubble{display:inline-block;max-width:80%;background:#DCF8C6;color:#111827;border-radius:10px 10px 2px 10px;padding:10px 12px;line-height:1.35;word-break:break-word;box-shadow:0 1px 1px rgba(0,0,0,.25);position:relative}
        .bubble:after{content:"";position:absolute;right:-4px;bottom:0;border-width:6px 0 0 6px;border-style:solid;border-color:transparent transparent transparent #DCF8C6;filter:drop-shadow(0 1px 0 rgba(0,0,0,.15))}
        .bubble-meta{font-size:11px;opacity:.6;margin-top:6px;text-align:right}
        .mail{background:#0B1220;border:1px solid #25304a;border-radius:14px;overflow:hidden;box-shadow:0 8px 24px rgba(0,0,0,.25)}
        .mail-head{background:#0E1526;border-bottom:1px solid #1E2B44;padding:12px 14px}
        .mail-row{display:flex;gap:10px;align-items:center;font-size:13px;color:#D1D5DB}
        .mail-dot{width:8px;height:8px;border-radius:50%;background:#6EE7B7}
        .mail-body{padding:14px;background:linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0));color:#E5E7EB}
        .mail-subject{font-weight:700;font-size:15px;margin-bottom:8px}
      `}</style>

      {/* CONTROLES */}
      <div className="flex items-center justify-between gap-3 mb-3">
        {varChips.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {varChips.map(c => (
              <span key={c} className="px-2.5 py-1 rounded-full text-xs border border-slate-600 bg-slate-800 text-slate-100" title={`{${c}}`}>
                {`{${c}}`}
              </span>
            ))}
          </div>
        ) : <span className="hint">Puedes usar variables como {`{nombre}`} o {`{fecha}`}</span>}

        <div className="flex items-center gap-2 text-xs">
          <span>Zoom</span>
          <input type="range" min={0.8} max={1.2} step={0.05} value={zoom} onChange={(e) => setZoom(parseFloat(e.target.value))} />
          <span className="w-10 text-right">{Math.round(zoom * 100)}%</span>
        </div>
      </div>

      {/* GRID PREVIEWS */}
      <div className={`grid grid-cols-1 ${showEmail ? "xl:grid-cols-2" : ""} gap-4`}>
        {!hideWhatsApp && (
          <div className="space-y-2">
            <label className="text-xs text-slate-400">Vista previa (WhatsApp)</label>
            <div className="flex justify-center xl:justify-start">
              <div style={{ transform: `scale(${zoom})`, transformOrigin: "top left" }}>
                <div className="phone">
                  <div className="phone-screen">
                    <div className="phone-header">
                      <div className="phone-avatar" />
                      <div>Contacto</div>
                    </div>
                    <div className="phone-chat">
                      <div className="bubble-row">
                        <div className="bubble">
                          <span dangerouslySetInnerHTML={{ __html: preview }} />
                          <div className="bubble-meta">12:45 ✓✓</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="hint">La vista previa es orientativa; WhatsApp aplica su propio parser.</div>
          </div>
        )}

        {/* Email */}
        {showEmail && (
          <div className="space-y-2">
            <label className="text-xs text-slate-400">Vista previa (Email)</label>
            <div style={{ transform: `scale(${zoom})`, transformOrigin: "top left" }}>
              <div className="mail">
                <div className="mail-head">
                  <div className="mail-row"><span className="mail-dot" /> <span>De:</span> <span className="font-medium">Tu Empresa &lt;no-reply@tuempresa.com&gt;</span></div>
                  <div className="mail-row"><span className="mail-dot" /> <span>Para:</span> <span className="font-medium">Contacto &lt;cliente@dominio.com&gt;</span></div>
                </div>
                <div className="mail-body">
                  <div className="mail-subject">{emailSubject || "(Sin asunto)"}</div>
                  <div className="prose max-w-none text-black" dangerouslySetInnerHTML={{ __html: emailPreview }} />
                </div>
              </div>
            </div>
            <div className="hint">Los clientes de correo pueden renderizar de forma distinta.</div>
          </div>
        )}
      </div>
    </section>
  );
}

export default TemplateEditor;   // 👈👈 Export default requerido
